/**
 * ExportHistoryDialog — download one device's history (events, location fixes,
 * direct release deploys) over a chosen window as a JSONL file meant to be
 * handed to an AI tool. Format + manifest: lib/historyExport.ts.
 *
 * Open to every role that can see the device: it exports exactly what the
 * device page already shows, and RLS scopes the rows server-side.
 *
 * The browser pages the backend and assembles the file itself, so a failed
 * page or Cancel discards everything fetched — a silently truncated export
 * would be worse than none, because the reader cannot tell it is incomplete.
 */
import { useMemo, useRef, useState } from 'react';
import { downloadHistoryExport, useHistoryExportSummary, useMe } from '../api/hooks';
import {
  EXPORT_SOURCES,
  buildManifest,
  exportFilename,
  saveBlob,
  selectedTotal,
  toJsonlParts,
  type ExportSource,
} from '../lib/historyExport';
import { EVENT_GLOSSARY } from '../lib/eventGlossary';
import { useToast } from './Toast';

const DAY = 24 * 3600 * 1000;
const PRESETS = [
  { key: '24h', label: '24 h', ms: DAY },
  { key: '7d', label: '7 d', ms: 7 * DAY },
  { key: '30d', label: '30 d', ms: 30 * DAY },
  { key: '90d', label: '90 d', ms: 90 * DAY },
  { key: '1y', label: '1 y', ms: 365 * DAY },
  // Predates the first fleet device; the window is clamped by the data itself.
  { key: 'all', label: 'All', ms: null },
] as const;
type PresetKey = (typeof PRESETS)[number]['key'] | 'custom';
const ALL_SINCE = '2020-01-01T00:00:00.000Z';

const SOURCE_LABEL: Record<ExportSource, string> = {
  events: 'Events & actions',
  locations: 'Location fixes',
  releases: 'Release deploys',
};

// <input type="datetime-local"> speaks local time without a zone.
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function presetRange(key: Exclude<PresetKey, 'custom'>): { since: string; until: string } {
  // Round `until` up to the next minute so reopening the dialog within the
  // same minute reuses the cached summary.
  const until = Math.ceil(Date.now() / 60_000) * 60_000;
  const p = PRESETS.find((x) => x.key === key)!;
  return {
    since: p.ms === null ? ALL_SINCE : new Date(until - p.ms).toISOString(),
    until: new Date(until).toISOString(),
  };
}

export function ExportHistoryControl({
  thing,
  friendlyName,
  tenantId,
  className,
  label = 'Export history…',
}: {
  thing: string;
  friendlyName: string | null;
  tenantId: string;
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className ?? 'text-accent text-sm hover:underline'}
        title="Download this device's events, location fixes and release deploys as a file"
      >
        {label}
      </button>
      {open && (
        <ExportHistoryDialog
          thing={thing}
          friendlyName={friendlyName}
          tenantId={tenantId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ExportHistoryDialog({
  thing,
  friendlyName,
  tenantId,
  onClose,
}: {
  thing: string;
  friendlyName: string | null;
  tenantId: string;
  onClose: () => void;
}) {
  const me = useMe();
  const toast = useToast();
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [range, setRange] = useState(() => presetRange('30d'));
  const [sources, setSources] = useState<ExportSource[]>([...EXPORT_SOURCES]);
  // null = all event types, including any that appear after the dialog opened.
  const [types, setTypes] = useState<string[] | null>(null);
  const [fetched, setFetched] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const rangeValid = Date.parse(range.until) > Date.parse(range.since);
  const summary = useHistoryExportSummary(rangeValid ? thing : undefined, range.since, range.until);
  const counts = summary.data?.counts;
  const eventTypes = useMemo(
    () => (counts ? Object.entries(counts.events).sort((a, b) => b[1] - a[1]) : []),
    [counts],
  );
  const busy = fetched !== null;
  const effectiveSources = sources.filter((s) => s !== 'events' || types === null || types.length > 0);
  const total = counts ? selectedTotal(counts, { sources: effectiveSources, types }) : null;

  const pickPreset = (key: Exclude<PresetKey, 'custom'>) => {
    setPreset(key);
    setRange(presetRange(key));
  };
  const setCustom = (field: 'since' | 'until', local: string) => {
    const ms = Date.parse(local); // datetime-local parses as local time
    if (Number.isNaN(ms)) return;
    setPreset('custom');
    setRange((r) => ({ ...r, [field]: new Date(ms).toISOString() }));
  };
  const toggleSource = (s: ExportSource) =>
    setSources((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : EXPORT_SOURCES.filter((x) => x === s || cur.includes(x))));
  const toggleType = (t: string) => {
    const all = eventTypes.map(([k]) => k);
    const cur = types ?? all;
    const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
    setTypes(next.length === all.length && all.every((k) => next.includes(k)) ? null : next);
  };

  const run = async () => {
    if (!counts || busy || !total) return;
    const selection = { since: range.since, until: range.until, sources: effectiveSources, types };
    const ctl = new AbortController();
    abort.current = ctl;
    setError(null);
    setFetched(0);
    try {
      const records = await downloadHistoryExport(thing, selection, setFetched, ctl.signal);
      const manifest = buildManifest({
        thing,
        friendlyName,
        tenantId,
        selection,
        counts,
        exportedBy: me.data?.email ?? me.data?.sub ?? null,
        exportedAt: new Date().toISOString(),
      });
      const blob = new Blob(toJsonlParts(manifest, records), { type: 'application/x-ndjson' });
      saveBlob(blob, exportFilename(thing, range.since, range.until));
      toast.success(`Exported ${records.length.toLocaleString()} records`);
      onClose();
    } catch (err) {
      if (ctl.signal.aborted) {
        setFetched(null);
        return;
      }
      setError(`Export failed — nothing was saved. ${(err as Error).message || ''}`.trim());
      setFetched(null);
    } finally {
      abort.current = null;
    }
  };

  const chip = (active: boolean) =>
    `px-2 py-0.5 text-xs rounded-full border ${
      active ? 'border-accent text-accent bg-accent/10' : 'border-border text-muted hover:bg-soft'
    }`;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-history-title"
    >
      <div className="card w-full max-w-lg space-y-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div>
          <h2 id="export-history-title" className="font-semibold">
            Export history
          </h2>
          <p className="text-xs text-muted mt-1">
            Downloads the history of <span className="font-mono">{thing}</span> as a JSONL file
            (one record per line). The first line describes the device, the window and every
            record type, so you can give the file to an AI assistant and ask questions about it.
          </p>
        </div>

        <fieldset className="space-y-2" disabled={busy}>
          <legend className="text-xs text-muted mb-1">Time span</legend>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => pickPreset(p.key)} className={chip(preset === p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              From
              <input
                type="datetime-local"
                value={toLocalInput(range.since)}
                onChange={(e) => setCustom('since', e.target.value)}
                className="rounded-md border border-border bg-transparent px-1.5 py-0.5"
              />
            </label>
            <label className="flex items-center gap-1">
              to
              <input
                type="datetime-local"
                value={toLocalInput(range.until)}
                onChange={(e) => setCustom('until', e.target.value)}
                className="rounded-md border border-border bg-transparent px-1.5 py-0.5"
              />
            </label>
          </div>
          {!rangeValid && <p className="text-xs text-warn">The end must be after the start.</p>}
        </fieldset>

        <fieldset className="space-y-1" disabled={busy}>
          <legend className="text-xs text-muted mb-1">Include</legend>
          {EXPORT_SOURCES.map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sources.includes(s)} onChange={() => toggleSource(s)} />
              <span>{SOURCE_LABEL[s]}</span>
              {counts && (
                <span className="text-xs text-muted">
                  {(s === 'events'
                    ? Object.values(counts.events).reduce((a, b) => a + b, 0)
                    : counts[s]
                  ).toLocaleString()}
                </span>
              )}
            </label>
          ))}
        </fieldset>

        {sources.includes('events') && (
          <fieldset className="space-y-1" disabled={busy}>
            <div className="flex items-center justify-between">
              <legend className="text-xs text-muted">Event types</legend>
              <span className="text-xs space-x-2">
                <button type="button" className="text-accent hover:underline" onClick={() => setTypes(null)}>
                  all
                </button>
                <button type="button" className="text-accent hover:underline" onClick={() => setTypes([])}>
                  none
                </button>
              </span>
            </div>
            {summary.isLoading && <p className="text-xs text-muted">Counting…</p>}
            {summary.error && <p className="text-xs text-warn">{(summary.error as Error).message}</p>}
            {counts && eventTypes.length === 0 && (
              <p className="text-xs text-muted">No events in this window.</p>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 max-h-48 overflow-y-auto">
              {eventTypes.map(([t, n]) => (
                <label key={t} className="flex items-center gap-2 text-xs" title={EVENT_GLOSSARY[t]?.meaning}>
                  <input type="checkbox" checked={types === null || types.includes(t)} onChange={() => toggleType(t)} />
                  <span className="font-mono truncate">{t}</span>
                  <span className="text-muted ml-auto">{n.toLocaleString()}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {busy && total !== null && (
          <div className="space-y-1" aria-live="polite">
            <div className="h-1.5 rounded-full bg-soft overflow-hidden">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${total ? Math.min(100, (fetched! / total) * 100) : 100}%` }}
              />
            </div>
            <p className="text-xs text-muted">
              {fetched!.toLocaleString()} / {total.toLocaleString()} records
            </p>
          </div>
        )}
        {error && <p className="text-xs text-warn">{error}</p>}

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted">
            {total !== null && !busy && `${total.toLocaleString()} records`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => (busy ? abort.current?.abort() : onClose())}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-soft"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={run}
              disabled={busy || !rangeValid || !counts || !total}
              className="px-3 py-1.5 text-sm rounded-md bg-accent text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Exporting…' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
